import { Controller } from "@hotwired/stimulus"

// Generic controller for auto-populating input fields based on a hash mapping
// Usage:
// <div data-controller="auto-populate" 
//      data-auto-populate-mapping-value='{"option1": "value1", "option2": "value2"}'>
//   <select data-action="change->auto-populate#updateTarget">
//     <option value="option1">Option 1</option>
//     <option value="option2">Option 2</option>
//   </select>
//   <input data-auto-populate-target="field" />
// </div>
export default class extends Controller {
  static targets = [ "field" ]
  static values = { 
    mapping: Object           // Hash of key-value pairs for mapping
  }

  declare readonly fieldTarget: HTMLInputElement
  declare readonly hasFieldTarget: boolean
  declare readonly mappingValue: Record<string, string>
  declare readonly hasMappingValue: boolean

  connect(): void {
    // Trigger initial population if there's already a value
    this.updateTarget()
  }

  updateTarget(event?: Event): void {
    // If triggered by an event, use the event target; otherwise, find the input/select within the controller element
    const sourceElement = event 
      ? event.currentTarget as HTMLInputElement | HTMLSelectElement
      : this.element.querySelector<HTMLInputElement | HTMLSelectElement>('select[data-action*="auto-populate#updateTarget"]')
    
    if (!sourceElement) {
      console.warn('Auto-populate controller: No source element found')
      return
    }

    if (!this.hasFieldTarget) {
      console.warn('Auto-populate controller: No target field found')
      return
    }

    const currentValue = sourceElement.value

    // Check if the current value exists in the mapping
    if (this.hasMappingValue && currentValue in this.mappingValue) {
      const newValue = this.mappingValue[currentValue]
      console.log(`Auto-populate: Setting field to '${newValue}' (from '${currentValue}')`)
      this.fieldTarget.value = newValue
      
      // Dispatch input event to notify other controllers/listeners
      this.fieldTarget.dispatchEvent(new Event('input', { bubbles: true }))
      this.fieldTarget.dispatchEvent(new Event('change', { bubbles: true }))
    } else {
      console.log(`Auto-populate: No mapping found for value '${currentValue}'`)
    }
  }
}
